import React from "react";
import {
  Drawer,
  Dialog,
  Slide,
  Box,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { BRAND } from "../auth/authTheme";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const DRAWER_WIDTH = { xs: "100%", sm: 480, md: 520 };

/**
 * Full-height right-side drawer for create/edit forms.
 * Note: MUI Drawer uses a portal — footer actions must call Formik's
 * submitForm() (not type="submit") to submit the form.
 */
const FormDrawer = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  width = DRAWER_WIDTH,
  progress,
  headerMeta,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (isMobile) {
    return (
      <Dialog
        fullScreen
        open={open}
        onClose={onClose}
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            bgcolor: BRAND.bg,
            display: "flex",
            flexDirection: "column",
            height: "100dvh",
            maxHeight: "100dvh",
            borderRadius: 0,
            m: 0,
          },
        }}
      >
        <Box
          sx={{
            bgcolor: "#fff",
            flexShrink: 0,
            borderBottom: `1px solid ${BRAND.border}`,
            pt: "env(safe-area-inset-top, 0px)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              py: 1,
              px: 0.5,
              pr: 2,
            }}
          >
            <IconButton
              sx={{ color: BRAND.navy }}
              onClick={onClose}
              aria-label="Close form"
            >
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "1.0625rem",
                  fontWeight: 700,
                  color: BRAND.navy,
                  lineHeight: 1.25,
                }}
              >
                {title}
              </Typography>
              {headerMeta && (
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: BRAND.muted,
                    fontWeight: 500,
                    mt: 0.125,
                  }}
                >
                  {headerMeta}
                </Typography>
              )}
            </Box>
          </Box>

          {typeof progress === "number" && (
            <Box
              sx={{
                height: 3,
                bgcolor: "#E2E8F0",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${progress}%`,
                  bgcolor: "var(--brand-orange, #E65A00)",
                  transition: "width 0.25s ease",
                }}
              />
            </Box>
          )}
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            px: 2,
            pt: 2,
            pb: 2,
            bgcolor: BRAND.bg,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            justifyContent: "flex-start",
          }}
        >
          {children}
        </Box>

        {footer && (
          <Box
            sx={{
              flexShrink: 0,
              bgcolor: "#fff",
              borderTop: `1px solid ${BRAND.border}`,
              boxShadow: "0 -4px 24px rgba(26, 43, 74, 0.08)",
              px: 2,
              pt: 1.5,
              pb: "calc(16px + env(safe-area-inset-bottom, 0px))",
              width: "100%",
            }}
          >
            {footer}
          </Box>
        )}
      </Dialog>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width,
          maxWidth: "100vw",
          display: "flex",
          flexDirection: "column",
          borderLeft: `1px solid ${BRAND.border}`,
          boxShadow: "-8px 0 32px rgba(0,0,0,0.12)",
          bgcolor: "#fff",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          px: 3,
          py: 2.5,
          borderBottom: `1px solid ${BRAND.border}`,
          flexShrink: 0,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: BRAND.navy, lineHeight: 1.3 }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: BRAND.muted, mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{
            color: BRAND.muted,
            mt: -0.5,
            "&:hover": { color: BRAND.navy, bgcolor: BRAND.bg },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 3,
          py: 2.5,
        }}
      >
        {children}
      </Box>

      {footer && (
        <>
          <Divider />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1.5,
              px: 3,
              py: 2,
              flexShrink: 0,
              bgcolor: "#fff",
              width: "100%",
            }}
          >
            {footer}
          </Box>
        </>
      )}
    </Drawer>
  );
};

export default FormDrawer;
